import React, { createContext, useContext, useEffect, useState } from 'react';
import localPreferencesService from '@/core/preferences/local-preferences-service';

export const MIN_TEXT_SCALE = 70;
export const MAX_TEXT_SCALE = 150;
export const DEFAULT_TEXT_SCALE = 100;

export function clampTextScale(value: number): number {
  if (Number.isNaN(value)) return DEFAULT_TEXT_SCALE;
  return Math.min(MAX_TEXT_SCALE, Math.max(MIN_TEXT_SCALE, value));
}

function applyTextScale(scale: number) {
  document
    .getElementById('html-root-element')
    ?.style.setProperty('--global-text-scale', String(scale / 100));
}

interface TextScaleContextType {
  scale: number;
  setScale: (value: number) => void;
}

const TextScaleContext = createContext<TextScaleContextType | undefined>(undefined);

export function TextScaleProvider({ children }: { children: React.ReactNode }) {
  const [scale, setScaleState] = useState(DEFAULT_TEXT_SCALE);

  useEffect(() => {
    localPreferencesService.getLocalPreferences().then(preferences => {
      const loaded = clampTextScale(preferences.globalTextScale ?? DEFAULT_TEXT_SCALE);
      setScaleState(loaded);
      applyTextScale(loaded);
    });
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      localPreferencesService.getLocalPreferences().then(preferences => {
        const remoteScale = clampTextScale(preferences.globalTextScale ?? DEFAULT_TEXT_SCALE);
        setScaleState(current => {
          if (current === remoteScale) return current;
          applyTextScale(remoteScale);
          return remoteScale;
        });
      });
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  const setScale = (value: number) => {
    const clamped = clampTextScale(value);
    setScaleState(clamped);
    applyTextScale(clamped);
    localPreferencesService.updateLocalPreferences(async preferences => {
      preferences.globalTextScale = clamped;
      return preferences;
    });
  };

  return (
    <TextScaleContext.Provider value={{ scale, setScale }}>{children}</TextScaleContext.Provider>
  );
}

export function useGlobalTextScale(): TextScaleContextType {
  const context = useContext(TextScaleContext);
  if (context === undefined) {
    return { scale: DEFAULT_TEXT_SCALE, setScale: () => {} };
  }
  return context;
}
